import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLang } from "@/i18n";

const HERO_IMG =
  "https://images.pexels.com/photos/36737326/pexels-photo-36737326.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const line1 = t("hero.l1");
  const line2 = t("hero.l2");
  const line3a = t("hero.l3a");
  const line3b = t("hero.l3b");
  const lines = [line1, line2, `${line3a} ${line3b}`.trim()].filter(Boolean);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Joueur sous les projecteurs"
          className="w-full h-[120%] object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0A0A0C] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32 pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-[#C5A059] text-xs sm:text-sm uppercase tracking-[0.4em] mb-8"
        >
          {t("hero.eyebrow")}
        </motion.p>

        <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl xl:text-9xl">
          {lines.map((line, i) => (
            <MaskedLine key={`${line}-${i}`} delay={0.35 + i * 0.15}>
              {i === lines.length - 1 ? (
                <>
                  {line3a ? `${line3a} ` : ""}<span className="text-[#C5A059]">{line3b}</span>
                </>
              ) : (
                line
              )}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-editorial italic text-lg md:text-xl text-[#A1A1AA] mt-8 max-w-xl"
        >
          "{t("hero.quote")}"
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap gap-5 mt-12"
        >
          <a
            href="/detection"
            data-testid="hero-cta-detection"
            className="group inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300"
          >
            {t("hero.cta1")}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#talents"
            data-testid="hero-cta-talents"
            className="inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 text-xs uppercase tracking-[0.25em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
          >
            {t("hero.cta2")}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#71717A]"
      >
        <ChevronDown size={22} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
